'use client'

import { useEffect, useMemo, useState, useCallback, useSyncExternalStore } from 'react'
import { usePathname } from 'next/navigation'
import dynamic from 'next/dynamic'
import { cva } from 'class-variance-authority'
import Link from '@/components/ui/Link'
import Pill from '@/components/ui/Pill'
import Container from '@/components/structure/Container'
import DesktopNavigation from '@/components/navigation/DesktopNavigation'
import MobileNavigation from '@/components/navigation/MobileNavigation'
import DesktopSearch from '@/components/search/DesktopSearch'
import BrandLogo from '@/components/ui/Brand'
import { cn } from '@/lib/utils'
import { navigation, trafficNavigation } from '@/data/navigation'
import { useHeaderContext } from '@/context/HeaderContext'
import { getNavCta } from '@/components/structure/header/nav-cta'
import { getPageTheme, subscribePageTheme } from '@/lib/pageTheme'
import { useIsLoggedIn } from '@/hooks/useIsLoggedIn'
import { ANALYTICS_ROUTES, isAnalyticsPath } from '@/lib/analytics-routes.mjs'

// Session state only resolves client-side (deferred fetch in useIsLoggedIn), so
// this can never render anything meaningful during SSR — load it on demand with
// ssr:false instead of shipping it in the initial header bundle. The `loading`
// placeholder reserves the avatar's exact footprint so swapping in the loaded
// chunk can't shift the surrounding nav (matches UserMenu's own
// `h-10 w-10 navigation:h-8 navigation:w-8` button).
const UserMenu = dynamic(() => import('@/components/navigation/UserMenu'), {
  ssr: false,
  loading: () => (
    <div
      aria-hidden
      className="h-10 w-10 shrink-0 rounded-full bg-gradient-to-tr from-primary-600 to-primary-300 navigation:h-8 navigation:w-8"
    />
  ),
})

type HeaderConfig = {
  theme?: 'dark' | 'light'
  position?: 'default' | 'overlay'
  inverted?: boolean
  shadow?: boolean
  pill?: boolean
}

const defaultConfig: HeaderConfig = {
  theme: 'light',
  position: 'overlay',
  inverted: false,
  pill: true,
}

const headerRules: { path: string; config: Partial<HeaderConfig>; exact?: boolean }[] = [
  { path: '/', config: { theme: 'light', shadow: true }, exact: true },
  { path: '/about', config: { theme: 'light', shadow: true }, exact: true },
  { path: '/deals', config: { theme: 'light', position: 'default' }, exact: true },
  { path: '/deals', config: { theme: 'dark' } },
  { path: '/apps', config: { theme: 'dark' } },
  { path: '/stacks', config: { theme: 'light', position: 'default' }, exact: true },
  { path: '/stacks', config: { theme: 'dark' } },
  { path: '/alternatives', config: { theme: 'light', position: 'default' }, exact: true },
  { path: '/alternatives', config: { theme: 'dark' } },
  { path: '/best', config: { theme: 'dark', inverted: true } },
  { path: '/compare', config: { theme: 'dark' } },
  { path: '/courses', config: { theme: 'dark' }, exact: true },
  { path: '/courses', config: { theme: 'light', position: 'default' } },
  { path: '/blog', config: { theme: 'light', position: 'default', pill: false } },
  { path: '/search', config: { theme: 'light', position: 'default', pill: false } },
  { path: '/careers', config: { theme: 'dark' } },
  { path: '/login', config: { theme: 'light', shadow: true }, exact: true },
  { path: ANALYTICS_ROUTES.root, config: { theme: 'light', shadow: true } },
]

export const getHeaderConfig = (pathname: string): HeaderConfig => {
  // First try to find an exact match
  const exactMatch = headerRules.find((rule) => rule.exact && pathname === rule.path)

  if (exactMatch) {
    return {
      ...defaultConfig,
      ...exactMatch.config,
    }
  }

  // Then try to find a prefix match (non-exact)
  const prefixMatch = headerRules.find((rule) => {
    if (rule.exact) return false
    return pathname === rule.path || pathname.startsWith(`${rule.path}/`)
  })

  return {
    ...defaultConfig,
    ...prefixMatch?.config,
  }
}

const variants = cva(
  'flex h-20 items-center z-50 fixed top-0 w-screen transition-colors duration-300 before:content-[""] before:absolute before:-z-10 before:backdrop-blur-sm before:transition-[opacity,height,width,left,border-radius,box-shadow,background-color] before:duration-500 before:ease-in-out before:will-change-[opacity,transform]',
  {
    variants: {
      theme: {
        dark: 'text-white before:border-white/15 before:bg-black/50 navigation:before:bg-transparent',
        light:
          'text-black before:border-gray-100 before:bg-white/95 navigation:before:bg-transparent dark:before:border-white/15 dark:before:bg-black/95',
        undefined: '',
      },
      position: {
        default:
          'navigation:border-gray-100 navigation:bg-white/95 navigation:dark:bg-black/95 navigation:dark:border-gray-800',
        overlay: '',
      },
      scrolled: {
        true: 'before:opacity-100',
        false: 'before:opacity-0',
      },
      fold: {
        true: 'before:border-b bg-white/95 before:bg-transparent dark:bg-black/95 before:rounded-none before:h-full before:w-full before:left-0',
        false:
          'before:h-14 before:rounded-[40px] before:border before:w-[calc(100%-8px)] before:left-1 before:shadow-xl navigation:before:shadow-none navigation:before:border-0 navigation:before:w-full navigation:before:left-0 navigation:before:rounded-none navigation:before:h-full',
      },
    },
    compoundVariants: [
      {
        position: 'default',
        scrolled: false,
        className: 'before:opacity-100',
      },
      {
        theme: 'dark',
        position: 'default',
        fold: true,
        className: 'navigation:bg-black/95',
      },
    ],
  },
)

export default function Base() {
  const { theme, setTheme } = useHeaderContext()
  const pathname = usePathname()
  const authState = useIsLoggedIn()
  const pageThemeOverride = useSyncExternalStore(subscribePageTheme, getPageTheme, () => null)

  const [scrolled, setScrolled] = useState(false)
  const [fold, setFold] = useState(false)
  const [contentTop, setContentTop] = useState<number | null>(null)

  const isTraffic = isAnalyticsPath(pathname)
  const resolvedNavigation = useMemo(
    () => (isTraffic ? trafficNavigation : navigation),
    [isTraffic],
  )
  const resolvedNavCta = useMemo(() => getNavCta(isTraffic), [isTraffic])

  const headerConfig = useMemo<HeaderConfig>(() => {
    return getHeaderConfig(pathname)
  }, [pathname])

  useEffect(() => {
    const updateContentTop = () => {
      const contentEl = document.getElementById('content')
      setContentTop(contentEl?.offsetTop ?? null)
    }

    updateContentTop()
    window.addEventListener('resize', updateContentTop)
    return () => window.removeEventListener('resize', updateContentTop)
  }, [pathname])

  useEffect(() => {
    if (headerConfig.inverted) {
      document.body.classList.add('dark')
      document.documentElement.style.backgroundColor = 'black'
    } else {
      document.body.classList.remove('dark')
      document.documentElement.style.backgroundColor = ''
    }
  }, [headerConfig.inverted])

  const handleScroll = useCallback(() => {
    const scrollY = window.scrollY
    setScrolled(scrollY > 80)
    if (headerConfig.pill !== false) {
      const threshold = isTraffic ? 1 : contentTop !== null ? contentTop - 80 : 560
      setFold(scrollY > threshold)
    }
  }, [contentTop, headerConfig.pill, isTraffic])

  useEffect(() => {
    let ticking = false

    const throttledScroll = () => {
      if (!ticking) {
        window.requestAnimationFrame(() => {
          handleScroll()
          ticking = false
        })
        ticking = true
      }
    }

    handleScroll()
    window.addEventListener('scroll', throttledScroll, { passive: true })
    return () => window.removeEventListener('scroll', throttledScroll)
  }, [handleScroll])

  useEffect(() => {
    setTheme(
      fold
        ? headerConfig.inverted
          ? 'dark'
          : 'light'
        : pageThemeOverride || headerConfig.theme || 'light',
    )
  }, [fold, setTheme, headerConfig.theme, headerConfig.inverted, pageThemeOverride])

  return (
    <header
      className={cn(
        variants({
          theme: theme,
          position: headerConfig.position,
          scrolled: headerConfig.pill === false ? undefined : scrolled,
          fold: headerConfig.pill === false ? true : fold,
        }),
      )}
    >
      <Container>
        <div className="flex items-center justify-between gap-2 navigation:gap-5">
          <Link href="/" aria-label="Home">
            <BrandLogo
              invert={theme === 'dark'}
              grayscale={theme === 'dark'}
              shadow={headerConfig.shadow}
              scrolled={scrolled}
            />
          </Link>

          <div className="flex w-full items-center gap-1.5 navigation:w-auto navigation:gap-3">
            <nav aria-label="Main navigation" className="hidden items-center navigation:flex">
              <DesktopNavigation links={resolvedNavigation} />
            </nav>

            <div className="relative z-30 w-full lg:ml-3 navigation:w-auto">
              <DesktopSearch scrolled={scrolled} />
            </div>

            <div className="relative z-20 hidden items-center gap-3 navigation:flex">
              <Link
                href={resolvedNavCta.href}
                className="hidden xl:block"
                aria-label={resolvedNavCta.ariaLabel}
              >
                <Pill as="span" style="default" theme={theme || 'light'} shine>
                  {resolvedNavCta.label}
                </Pill>
              </Link>

              <div className="flex items-center">
                {authState === 'authenticated' ? (
                  <UserMenu />
                ) : (
                  <Link href="/login?signup=true" aria-label="Sign up">
                    <Pill
                      as="span"
                      style={theme === 'dark' ? 'glass' : 'outline'}
                      theme={theme || 'light'}
                    >
                      Sign up
                    </Pill>
                  </Link>
                )}
              </div>
            </div>

            <nav aria-label="Mobile navigation" className="flex items-center navigation:hidden">
              <MobileNavigation links={resolvedNavigation} isTraffic={isTraffic} />
            </nav>

            {authState === 'authenticated' && (
              <div className="relative z-20 flex items-center navigation:hidden">
                <UserMenu />
              </div>
            )}
          </div>
        </div>
      </Container>
    </header>
  )
}
